import { JobDetailItem } from "@/components/jobDetail/job-detail";
import { trpc } from "@/trpc/react";
import { useLocalSearchParams, useRouter } from "expo-router";
import Drawer from "expo-router/drawer";
import { ActivityIndicator, RefreshControl, ScrollView, View, StyleSheet, Text } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useRefetchOnFocus } from "@/hooks/useRefetchOnFocus";
import { useCrew } from "@/context/CrewProvider";
import colors from '@/constants/Colors';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { TouchableOpacity } from "react-native-gesture-handler";
import { Ionicons } from '@expo/vector-icons';
import DatePickerModal from "@/components/jobDetail/date-picker-modal";
import { formatLocalTime } from "shared-utils";
import { addHours } from "date-fns";

export default function Page() {
  const insets = useSafeAreaInsets();
  const { id } = useLocalSearchParams();
  const router = useRouter();
  const { currentCrewMember } = useCrew();
  const jobDateId = Number(id);
  const [isDatePickerVisible, setIsDatePickerVisible] = useState(false);

  const { data: jobDataResult, isLoading, refetch: refetchJobData } = trpc.jobDateListRouter.getById.useQuery(jobDateId);
  const jobData = useMemo(() => jobDataResult?.data, [jobDataResult?.data]);

  const { data: crewListResult, isLoading: crewListLoading, refetch: refetchCrewList } = trpc.crew.getCrewListForJobDate.useQuery(jobDateId);
  const crewList = useMemo(() => crewListResult, [crewListResult]);

  const { data: accidentListResult, isLoading: accidentListLoading, refetch: refetchAccidentList } = trpc.accident.getByJobDate.useQuery(jobDateId);
  const accidentList = useMemo(() => accidentListResult, [accidentListResult]);

  const { data: vehiclesResult, isLoading: vehiclesLoading, refetch: refetchVehicles } = trpc.vehicle.getVehiclesForJobDate.useQuery(jobDateId);
  const vehicles = useMemo(() => vehiclesResult, [vehiclesResult]);

  const { data: jobDateListResult } = trpc.jobDateListRouter.listForJobId.useQuery(
    jobData?.job_id || 0,
    { enabled: Boolean(jobData?.job_id) }
  );

  const { data: jobDocumentsResult, refetch: refetchJobDocuments } = trpc.document.getJobDocuments.useQuery(jobData?.job_id || 0, { enabled: Boolean(jobData?.job_id) });
  const { data: messageDocumentsResult, refetch: refetchMessageDocuments } = trpc.document.getMessageDocuments.useQuery(jobData?.job_id || 0, { enabled: Boolean(jobData?.job_id) });
  const { data: emailDocumentsResult, refetch: refetchEmailDocuments } = trpc.document.getEmailDocuments.useQuery(jobData?.job_id || 0, { enabled: Boolean(jobData?.job_id) });

  const jobDateList = useMemo(() => jobDateListResult, [jobDateListResult]);

  // bit of a hack as we don't want to load [id]/index as the title
  const [title, setTitle] = useState('');

  useEffect(() => {
    if (jobData && !isLoading) {
      setTitle(`Job #${jobData.job_id}`);
    }
  }, [jobData?.job_id, isLoading]);

  const openDatePicker = useCallback(() => {
    setIsDatePickerVisible(true);
  }, []);

  const closeDatePicker = useCallback(() => {
    setIsDatePickerVisible(false);
  }, []);

  // Memoize the refetch function to stabilize its reference
  const refetch = useCallback(() => {
    refetchJobData();
    refetchCrewList();
    refetchAccidentList();
    refetchVehicles();
    refetchMessageDocuments();
    refetchJobDocuments();
    refetchEmailDocuments();
  }, [refetchJobData, refetchCrewList, refetchAccidentList, refetchVehicles, refetchMessageDocuments, refetchJobDocuments, refetchEmailDocuments]);

  useRefetchOnFocus(refetch);

  const isRefetching = isLoading || crewListLoading || accidentListLoading || vehiclesLoading;
  const showDatePicker = jobDateList && jobDateList.data && jobDateList.data.length > 0 &&
    jobData && (
      // Check if crew member is marked as chief for this job date
      crewList?.find(crew => crew.crew_id === currentCrewMember?.id)?.is_chief ||
      // Check if crew member's rank is <= 18 (higher authority)
      (currentCrewMember?.crew_levels?.[0]?.level?.rank !== null &&
        currentCrewMember?.crew_levels?.[0]?.level?.rank !== undefined &&
        currentCrewMember.crew_levels[0].level.rank <= 18)
    );

  // Process date list items to include hours and time information matching JobDetailItem
  const processedDates = useMemo(() => {
    // Ensure processedDates is always an array, defaulting to empty
    return (jobDateList?.data || []).map((date: any) => {
      let displayHours = '';
      let displayStartTime = '';
      let displayEndTime = '';

      // Format the time in the same way JobDetailItem does
      try {
        if (date.date_started_time != null && date.date_hours_needed) {
          // Use the same methods as mapJobDateCrewDetailForFrontend
          displayStartTime = formatLocalTime(date.date_started_at, "HH:mm");
          displayEndTime = formatLocalTime(addHours(date.date_started_at, date.date_hours_needed), "HH:mm");
          displayHours = `${date.date_hours_needed}hrs`;
        }
      } catch (error) {
        // Silent fail
      }

      return {
        date_id: date.date_id,
        date_started_at: date.date_started_at,
        venue_name: typeof date.venue_name === 'string' ? date.venue_name : "Unknown venue",
        venue_address_line_1: typeof date.venue_address_line_1 === 'string' ? date.venue_address_line_1 : "",
        display_hours: displayHours,
        display_start_time: displayStartTime,
        display_end_time: displayEndTime
      };
    });
  }, [jobDateList?.data]);

  // Handle date selection and navigation
  const handleDateSelect = useCallback((dateId: number) => {
    closeDatePicker();
    if (dateId !== jobDateId) {
      router.push({
        pathname: "/home/[id]",
        params: { id: dateId.toString() }
      });
    }
  }, [closeDatePicker, jobDateId, router]);

  // Header right component with date count
  const HeaderRight = useCallback(() => {
    if (showDatePicker && processedDates && processedDates.length > 1) {
      return (
        <TouchableOpacity
          style={styles.headerButton}
          onPress={openDatePicker}
        >
          <Ionicons name="calendar-outline" size={22} color="#FFF000" />
          <View style={styles.countBadge}>
            <Text style={styles.countText}>{processedDates.length}</Text>
          </View>
        </TouchableOpacity>
      );
    }
    return null;
  }, [showDatePicker, processedDates, openDatePicker]);

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <Drawer.Screen
        options={{
          headerTitle: title || 'Job Details',
          headerRight: HeaderRight
        }}
      />
      <View style={styles.container}>
        {isLoading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#888888" />
          </View>
        ) : (
          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={[styles.scrollContent, { paddingBottom: 32 + insets.bottom }]}
            refreshControl={
              <RefreshControl refreshing={isRefetching} onRefresh={refetch} />
            }
          >
            {jobData ? (
              <JobDetailItem
                item={jobData}
                crewList={crewList || []}
                accidentList={accidentList}
                vehicles={vehicles}
                hideShowDates={true}
                documents={jobDocumentsResult?.data}
                messageDocuments={messageDocumentsResult?.data}
                emailDocuments={emailDocumentsResult?.data}
              />
            ) : null}
          </ScrollView>
        )}

        {/* Date Picker Modal */}
        {processedDates.length > 0 && (
          <DatePickerModal
            visible={isDatePickerVisible}
            onClose={closeDatePicker}
            dates={processedDates}
            currentDateId={jobDateId}
            onDateSelect={handleDateSelect}
          />
        )}
      </View>
    </GestureHandlerRootView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f7',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  scrollView: {
    flex: 1,
    backgroundColor: '#f5f5f7',
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 32,
  },
  headerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
    marginRight: 8,
  },
  countBadge: {
    backgroundColor: colors.accent,
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 4,
    paddingHorizontal: 4,
  },
  countText: {
    color: '#000',
    fontSize: 12,
    fontWeight: 'bold',
  }
});
